'use client'
import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Link from "@mui/material/Link";
import Grid2 from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import { Divider, Box } from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";

import { auth } from "config/firebase";
import {
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import { signInWithGoogle } from "_DatabaseFunctions/cloudCalls/auth";
import { getAuthErrorMessage, validateAuthInput } from "utils/authErrorMessages";

interface SignInProps {
  setPassReset?: () => void;
  setSignup?: () => void;
}

interface FormValues {
  email: string;
  password: string;
  remember: boolean;
}

export default function SignIn(props: SignInProps): JSX.Element {
  const [errMsg, setErrMsg] = useState<string>("");
  const [values, setValues] = useState<FormValues>({
    email: "",
    password: "",
    remember: false,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
  };

  const toggleInput = (): void => {
    setValues({ ...values, remember: !values.remember });
  };

  const login = async (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    e.preventDefault();
    setErrMsg(""); // Clear any previous errors

    // Validate input before attempting sign in
    const validationErrors = validateAuthInput({
      email: values.email,
      password: values.password,
    });

    if (Object.keys(validationErrors).length > 0) {
      const firstError = Object.values(validationErrors)[0];
      setErrMsg(firstError);
      return;
    }

    try {
      const persistence = values.remember
        ? browserLocalPersistence
        : browserSessionPersistence;

      await setPersistence(auth, persistence);
      await signInWithEmailAndPassword(auth, values.email, values.password);
      // User will be redirected by Auth component useEffect
    } catch (error: any) {
      console.error("Sign-in error:", error);
      const userFriendlyMessage = getAuthErrorMessage(error);
      setErrMsg(userFriendlyMessage);
    }
  };

  const handleGoogleSignIn = async (): Promise<void> => {
    try {
      setErrMsg("");
      await signInWithGoogle(values.remember);
      // User will be redirected by Auth component useEffect
    } catch (error: any) {
      console.error("Google sign-in error:", error);
      const userFriendlyMessage = getAuthErrorMessage(error);
      setErrMsg(userFriendlyMessage);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
      }}
    >
      <Avatar
        sx={{
          mt: 3,
          mb: 2,
          backgroundColor: "primary.main",
        }}
      />
      <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
        Sign In
      </Typography>
      <Box component="form" noValidate sx={{ width: "100%" }}>
        <TextField
          value={values.email}
          onChange={handleInputChange}
          variant="outlined"
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email Address"
          name="email"
          autoComplete="email"
          autoFocus
        />
        <TextField
          value={values.password}
          onChange={handleInputChange}
          variant="outlined"
          margin="normal"
          required
          fullWidth
          name="password"
          label="Password"
          type="password"
          id="password"
          autoComplete="current-password"
        />
        <FormControlLabel
          control={
            <Checkbox
              onChange={toggleInput}
              value={!values.remember}
              color="secondary"
            />
          }
          label="Remember me"
        />
        <Button
          onClick={login}
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          sx={{ mt: 3, mb: 2 }}
        >
          Sign In
        </Button>

        {errMsg && (
          <Typography
            variant="body2"
            color="error"
            sx={{ mb: 2, textAlign: "center" }}
          >
            {errMsg}
          </Typography>
        )}

        <Box sx={{ my: 2 }}>
          <Divider>
            <Typography variant="body2" color="textSecondary">
              or
            </Typography>
          </Divider>
        </Box>

        <Button
          onClick={handleGoogleSignIn}
          fullWidth
          variant="outlined"
          startIcon={<GoogleIcon />}
          sx={{ mb: 2 }}
        >
          Continue with Google
        </Button>

        <Grid2 container>
          <Grid2 size="grow">
            <Link 
              component={RouterLink} 
              to="/reset-password" 
              variant="body2"
              onClick={props.setPassReset}
            >
              Forgot password?
            </Link>
          </Grid2>
          <Grid2>
            <Link 
              component={RouterLink} 
              to="/signup" 
              variant="body2"
              onClick={props.setSignup}
            >
              Sign Up
            </Link>
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
}
